import { action, computed, observable, runInAction } from "mobx";

import SearchApiService from "@api-services/search/SearchApiService";
import { SuggestedSearchListItem } from "@data-types/suggested-search";
import { history, redirect, redirectPreserveSearchParams } from "@state";
import { getCurrentCulture } from "@utils";

import { Period } from "@state/common/PeriodConverter";
import { SearchUtility } from "@state/stores/offer/search/SearchUtility";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { RootStore } from "@state/stores/types";
import { StorageStateKeysEnum } from "@utils/storage";
import { SuggestedSearchResponse } from "@api-types/suggested-search";
import { analyticsTracker } from "@utils/analytics";

type TConfig = {
	onTermChange?: (searchTerm: string) => void;
};

export enum SearchAction {
	SEARCH,
	PICK_FIRST,
	PICK,
	EVENT_PICK,
}

export type TSuggestedItem = {
	displayText: string;
	tournamentId?: string;
	categoryId?: string;
	sportId?: string;
	eventId?: string;
};

export default class SearchStore {
	minLengthForFilterSuggestedList = 1;
	minLengthForFreeSearchSubmit = 3;
	searchValidation = /^[A-Za-z0-9]{1,20}$/;
	resultMaxSize = 10;

	@observable fullSuggestedSearchList: SuggestedSearchListItem[] | null =
		null;
	@observable _filteredSearchList: SuggestedSearchListItem[] | null = null;

	@observable searchTerm: string = "";
	@observable public persistentTerm: string;

	@observable private _isSearchOpen = false;
	@observable private _isSuggestedListOpen = false;

	private config: TConfig = {};
	previousQuery: string;

	rootStore: RootStore;

	constructor(rootStore: RootStore, config: TConfig = {}) {
		this.rootStore = rootStore;
		this.config = config;
		this.onSubmit = this.onSubmit.bind(this);
	}

	get isSearchOpen(): boolean {
		return this._isSearchOpen;
	}

	get isSuggestedListOpen(): boolean {
		return this._isSuggestedListOpen;
	}

	@computed get trimSearchTerm() {
		return this.searchTerm ? this.searchTerm.trim() : "";
	}

	@computed get applySearch() {
		return this.searchTerm != null && this.trimSearchTerm !== "";
	}

	@computed get filteredSearchList() {
		return this._filteredSearchList?.slice(0, this.resultMaxSize);
	}

	@action.bound
	setIsSuggestedListOpen(isOpen: boolean) {
		this._isSuggestedListOpen = isOpen;
	}

	@action.bound
	setIsSearchOpen(isOpen: boolean) {
		this._isSearchOpen = isOpen;
	}

	@action.bound
	public async onChange(term: string) {
		this.searchTerm = term;

		runInAction(() => {
			redirect(
				`/${getCurrentCulture()}${DefaultPath.SEARCH}/${this.trimSearchTerm}`
			);
		});
	}

	@action.bound
	setValue(searchTerm: string) {
		this.searchTerm = searchTerm;

		if (this.config.onTermChange) {
			this.config.onTermChange(this.trimSearchTerm);
		}
	}

	@action.bound
	searchButtonOnClick() {
		this.setIsSearchOpen(true);
		this.setIsSuggestedListOpen(true);
		analyticsTracker.trackSearchInputClick();
		if (
			!this.searchTerm.trim().length &&
			!this._filteredSearchList?.length
		) {
			this._filteredSearchList = this.fullSuggestedSearchList;
		}
	}

	@action.bound
	onReset() {
		this.searchTerm = "";

		if (this.config.onTermChange) {
			this.config.onTermChange("");
		}
	}

	@action.bound
	onTermChange(term: SearchStore["persistentTerm"]) {
		this.searchTerm = term;
		if (this.searchTerm?.length < this.minLengthForFilterSuggestedList) {
			this._filteredSearchList = null;
		} else {
			this._filterSuggestedList(this.searchTerm);
		}
	}

	@action.bound
	async getSuggestedSearchList(): Promise<void> {
		try {
			const response = await SearchApiService.getSuggestedSearch();
			if (this._isSuggestedListResponseValid(response)) {
				if (this._shouldUpdateSuggestedList(response.version)) {
					const suggestedTransformedList =
						SearchUtility.transformSearchList(response);
					this.fullSuggestedSearchList = suggestedTransformedList;
					this._updateStorageSuggestedList(
						suggestedTransformedList,
						response.version
					);
				}
				if (!this.fullSuggestedSearchList) {
					this.fullSuggestedSearchList =
						this._getStorageSuggestedList();
				}
				if (
					!this._filterSuggestedList.length &&
					this.searchTerm == ""
				) {
					this._filteredSearchList = this.fullSuggestedSearchList;
				}
			} else {
				this.fullSuggestedSearchList = [];
			}
		} catch (error) {
			console.error("Error fetching suggested search list:", error);
			this.fullSuggestedSearchList = [];
		}
	}

	@action.bound
	searchSuggestedItem(action: SearchAction, item: TSuggestedItem) {
		this.onTermChange(item.displayText);
		return this.onSubmit({
			item,
			action,
			period: "full",
		});
	}

	@action.bound
	onSubmit(
		{
			item,
			action,
			period,
		}: {
			item?: TSuggestedItem;
			action: SearchAction;
			period: Period;
		},
		submitFn?: (() => void) | null
	) {
		this.persistentTerm = this.trimSearchTerm;

		analyticsTracker.trackSearchType(action, this.persistentTerm);

		if (submitFn != null) {
			submitFn();
		} else {
			if (action == SearchAction.EVENT_PICK && item?.eventId) {
				this.redirectToEvent(item?.eventId);

				this.resetSearch();

				return true;
			}

			if (this.trimSearchTerm === "") {
				this.resetSearch();

				return false;
			}

			if (
				(action == SearchAction.SEARCH ||
					action == SearchAction.PICK_FIRST) &&
				this.trimSearchTerm.length < this.minLengthForFreeSearchSubmit
			) {
				this.resetSearch();

				return true;
			}

			const searchParams = new URLSearchParams();
			searchParams.append("action", String(action));

			if (action == SearchAction.PICK && item) {
				if (item.sportId)
					searchParams.append(
						"sportId",
						JSON.stringify(item.sportId)
					);
				if (item.categoryId)
					searchParams.append(
						"categoryId",
						JSON.stringify(item.categoryId)
					);
				if (item.tournamentId)
					searchParams.append(
						"tournamentId",
						JSON.stringify(item.tournamentId)
					);
			}

			redirectPreserveSearchParams(
				`/${getCurrentCulture()}/search/${period}/${
					this.persistentTerm
				}?${searchParams}`,
				history.location.pathname.includes(DefaultPath.SEARCH)
			);
		}

		this.resetSearch();

		this.setIsSearchOpen(false);
		return true;
	}

	@action.bound
	private _filterSuggestedList(query: string) {
		if (
			!query?.trim() ||
			!this.fullSuggestedSearchList ||
			this.fullSuggestedSearchList.length <
				this.minLengthForFilterSuggestedList
		) {
			this._clearSearchResult();
			this._filteredSearchList = this.fullSuggestedSearchList;
			return;
		}

		const lowerCaseQuery = query.toLowerCase();

		const shouldUsePrevious =
			this._shouldUsePreviousResults(lowerCaseQuery);

		const searchBase = shouldUsePrevious
			? this._filteredSearchList
			: this.fullSuggestedSearchList;

		if (searchBase != null) {
			this._filteredSearchList = SearchUtility.searchItems(
				searchBase,
				lowerCaseQuery,
				this.minLengthForFilterSuggestedList
			);
		}

		this._updateQueryHistory(lowerCaseQuery);
	}

	@action.bound
	public resetSearch() {
		this.searchTerm = "";
		this._filteredSearchList = this.fullSuggestedSearchList;
		this.setIsSearchOpen(false);
		this.setIsSuggestedListOpen(false);
	}

	public redirectToEvent(eventId: string) {
		this.resetSearch();
		redirect(
			`/${getCurrentCulture()}/${DefaultPath.PREMATCH}/event/${eventId}`
		);
	}

	private _shouldUsePreviousResults(newQuery: string): boolean {
		return (
			newQuery.startsWith(this.previousQuery) &&
			newQuery.length > this.previousQuery.length
		);
	}

	private _updateQueryHistory(query: string) {
		this.previousQuery = query;
	}

	@action.bound
	private _clearSearchResult() {
		this._filteredSearchList = [];
		this.previousQuery = "";
	}

	private _isSuggestedListResponseValid(
		response: SuggestedSearchResponse | null
	): boolean {
		if (
			response?.items &&
			response.items.length > 0 &&
			response.lookup &&
			response.version
		) {
			return true;
		}
		return false;
	}

	private _shouldUpdateSuggestedList(newVersion: string): boolean {
		const currentVersion = this.rootStore.localStorageProvider.get(
			StorageStateKeysEnum.OFFER_SUGGESTED_SEARCH_VERSION
		) as string;
		return currentVersion != newVersion;
	}

	private _updateStorageSuggestedList(
		suggestedList: SuggestedSearchListItem[],
		version: string
	) {
		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.OFFER_SUGGESTED_SEARCH_LIST,
			JSON.stringify(suggestedList)
		);
		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.OFFER_SUGGESTED_SEARCH_VERSION,
			version
		);
	}

	private _getStorageSuggestedList(): SuggestedSearchListItem[] | null {
		return JSON.parse(
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.OFFER_SUGGESTED_SEARCH_LIST
			) as string
		) as SuggestedSearchListItem[];
	}
}
